import { useTheme } from "@amx/ux-twoui-theming";
import { ActionButton as FluentActionButton } from "@fluentui/react";
import React from "react";
import { MenuIconButton, MenuItem } from "../../../components/Buttons/MenuIconButton/MenuIconButton";
import { Icon } from "../../../components/Icon/Icon";
import { SlotStack, SlotStackItem } from "../../../components/Stacks/SlotStack/SlotStack";
import { Stack, StackItem } from "../../../components/Stacks/Stack/Stack";
import { Text } from "../../../components/Text/Text";
import { PerViewportWithDefault } from "../../../shared/constants";
import { useComponentLogger } from "../../../shared/logging";
import { InteractiveElementProps } from "../../../shared/props";
import { useViewport } from "../../../styles";
import { TextTypes } from "../../../styles/fonts";
import { getMenuItemStyles, getStyles } from "./ListHeaderWithColumns.styles";

export interface ColumnProps extends InteractiveElementProps {
    /**
     * Text to rendered in the column
     */
    readonly text: string;
    /**
     * Callback function for column click
     */
    readonly onClick: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => boolean | void;
}

type SlotSizes = readonly number[];
export type SlotSizesPerViewportWithDefault = PerViewportWithDefault<SlotSizes>;

export type SortType = "asc" | "desc";

export interface ListHeaderWithColumnsProps {
    readonly totalSlots: number;
    readonly columns: readonly ColumnProps[];
    readonly columnSlotSize: SlotSizesPerViewportWithDefault;
    readonly sortedOn: string;
    readonly sortType: SortType;
    readonly menuHeader?: string;
    readonly menuButtonAriaLabel: string;
    readonly menuButtonDataBiId: string;
}

export const ListHeaderWithColumns: React.FC<ListHeaderWithColumnsProps> = ({
    columns,
    totalSlots,
    columnSlotSize,
    sortedOn,
    sortType,
    menuHeader,
    menuButtonAriaLabel,
    menuButtonDataBiId
}) => {
    useComponentLogger("ListHeaderWithColumns");

    const viewport = useViewport();
    const theme = useTheme();
    const slotsCount = columnSlotSize[viewport] || columnSlotSize.default;

    if (!slotsCount) {
        // BugId: 36912631, no throw in components.
        // tslint:disable-next-line: no-throw
        throw Error(`ListHeaderWithColumns: default or viewport slotsCount is not defined`);
    }

    const renderColumn = (column: ColumnProps, index: number) => {
        //A method to map the signature of the passed in onClick to that of Fluent's ActionButton
        const fluentActionButtonShim = (ev: React.MouseEvent<HTMLElement, MouseEvent>) => (
            column.onClick(ev as React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>)
        );
        return (
            <FluentActionButton onClick={fluentActionButtonShim} aria-label={column["aria-label"]} styles={getStyles(index, theme)}>
                <ColumnWithSortIcon name={column.text} variant="headerText" isSorted={sortedOn === column.text} sortType={sortType} />
            </FluentActionButton>
        );
    };

    const onClickMenuItem = (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) => (
        () => {
            item.onClick();
            dismissMenu();
        }
    );

    const onRenderMenuItem = (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) => (
        <FluentActionButton onClick={onClickMenuItem(item, dismissMenu)} styles={getMenuItemStyles(theme)}>
            <ColumnWithSortIcon name={item.name} variant="headerText" isSorted={sortedOn === item.name} sortType={sortType} />
        </FluentActionButton>
    );

    const menuItems: readonly MenuItem[] = columns.slice(1).map(column => ({
        key: column.text,
        name: column.text,
        onClick: column.onClick,
        onRender: onRenderMenuItem,
        "aria-label": column["aria-label"],
        "data-bi-id": column["data-bi-id"]
    }));

    const menuHeaderItem = (menuHeaderText: string): MenuItem[] => ([{
        key: menuHeaderText,
        name: menuHeaderText,
        itemType: "header",
        backgroundColor: "neutralHigh"
    }, {
        itemType: "divider"
    }]);

    const computedMenuItems = menuHeader ?
        [...menuHeaderItem(menuHeader), ...menuItems] : [...menuItems];

    const headerWithMenu = (
        <Stack horizontal={true} horizontalAlign="space-between" verticalAlign="center">
            <StackItem grow={true}>
                {renderColumn(columns[0], 0)}
            </StackItem>
            <MenuIconButton
                iconName="MoreVertical"
                menuItems={computedMenuItems}
                isBeakVisible={false}
                iconColor="neutralPrimary"
                aria-label={menuButtonAriaLabel}
                data-bi-id={menuButtonDataBiId}
            />
        </Stack>
    );

    return (
        ["s", "m", "l"].includes(viewport) && columns.length > 1 ? headerWithMenu : (
            <SlotStack totalSlots={totalSlots}>
                {columns.map((column, index) => (
                    <SlotStackItem key={index} slots={slotsCount[index]} align={"center"}>
                        {renderColumn(column, index)}
                    </SlotStackItem>
                ))}
            </SlotStack>
        )
    );
};

interface ColumnWithSortIconProps {
    readonly name: string;
    readonly variant: TextTypes;
    readonly isSorted: boolean;
    readonly sortType: SortType;
}

//28224611 - Change the default icons to sortUp and sortDown icons once they are available
const ColumnWithSortIcon: React.FC<ColumnWithSortIconProps> = ({ name, variant, isSorted, sortType }) => (
    <Stack horizontal={true} height="42px" verticalAlign="center" childrenGap="8px">
        <StackItem>
            <Text variant={variant}>{name}</Text>
        </StackItem>
        {isSorted && (
            <StackItem>
                <Icon
                    iconName={sortType === "asc" ? "SortUp" : "SortDown"}
                    size="12px"
                />
            </StackItem>
        )}
    </Stack>
);
